/**
 * Closing the shift and counting the drawer.
 *
 * WHY THE EXPECTED AMOUNT IS NOT SHOWN WHILE COUNTING
 * ---------------------------------------------------
 * This is the one control in the whole system that catches money going
 * missing, and it only works if the count is honest.
 *
 * Show a cashier what the drawer *should* hold and a tired person at the end
 * of a long shift will type that number in and go home. The count then agrees
 * perfectly with the expectation every single day, and a real shortfall is
 * invisible — worse than not counting at all, because it looks like evidence.
 *
 * So the drawer is counted blind. The expected figure and the difference
 * appear afterwards, once the count is committed and can no longer be
 * adjusted to match.
 *
 * WHY IT IS COUNTED BY DENOMINATION
 * ---------------------------------
 * Asking for one total invites a guess. Counting notes in stacks is what
 * someone is doing anyway, and the arithmetic is then the computer's job —
 * which is where the errors were coming from.
 */
import { useState } from 'react';
import { formatPKR } from '@foodrun/shared';

import { DENOMINATIONS, countTotal, toPayload } from './denominations.js';
import { Button } from '../../components/ui/Button.jsx';
import { ShiftReport } from './ShiftReport.jsx';

export function ShiftClosePanel({ shift, onClose, onCancel, onFinish, busy, error, result }) {
  const [counts, setCounts] = useState({});
  const [notes, setNotes] = useState('');

  const counted = countTotal(counts);

  // The close came back saying there are unpaid bills. The next attempt says so
  // on purpose (closeWithUnpaidBills), which hands them over rather than being
  // refused again.
  const unpaidBlock = error?.code === 'UNPAID_BILLS';
  const unpaidCount = error?.details?.count ?? 0;
  const outstanding = error?.details?.outstanding ?? null;

  const closeBody = (handOver) => ({
    actualCash: counted,
    denominations: toPayload(counts),
    ...(notes.trim() ? { notes: notes.trim() } : {}),
    ...(handOver ? { closeWithUnpaidBills: true } : {}),
  });

  // Once closed, the numbers can be shown: the count is committed. Finishing
  // here hands the till over — it signs out — rather than returning to the
  // same person's till. Falls back to a plain dismiss if no handover was wired.
  if (result) return <ShiftClosed result={result} onDone={onFinish ?? onCancel} />;

  function set(key, value) {
    setCounts((current) => ({ ...current, [key]: value }));
  }

  return (
    <div className="mx-auto max-w-lg p-4">
      <h1 className="font-semibold text-xl text-ink">
        Close your shift
        {shift?.shiftName && <span className="capitalize text-ink-3"> · {shift.shiftName}</span>}
      </h1>
      <p className="mt-1.5 text-sm text-ink-2">
        Count everything in the drawer. The figures are compared once you are done.
      </p>

      <dl className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-rule bg-rule">
        <Stat label="Orders" value={shift?.orderCount ?? 0} />
        <Stat label="Sales" value={formatPKR(shift?.totalSales ?? 0)} />
        <Stat label="Opened with" value={formatPKR(shift?.openingAmount ?? 0)} />
        {/* Deliberately NOT cashInDrawer, which is what the count is measured
            against — see the note at the top of this file. */}
      </dl>

      <div className="mt-5 rounded-xl border border-rule bg-surface p-4">
        <h2 className="text-sm font-medium text-ink-2">Count the drawer</h2>

        <div className="mt-3 space-y-2">
          {DENOMINATIONS.map((note) => (
            <div key={note.key} className="flex items-center gap-3">
              <span className="w-20 shrink-0 text-sm tabular-nums text-ink-2">{note.label}</span>
              <span aria-hidden="true" className="text-ink-3">
                ×
              </span>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                aria-label={`How many ${note.label} notes`}
                value={counts[note.key] ?? ''}
                onChange={(e) => set(note.key, e.target.value)}
                placeholder="0"
                className="h-11 w-20 rounded-lg border border-rule bg-surface-3 px-3 text-ink text-center tabular-nums focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
              <span className="ml-auto text-sm tabular-nums text-ink-3">
                {(Number(counts[note.key]) || 0) > 0
                  ? formatPKR(Math.max(0, Math.floor(counts[note.key])) * note.value)
                  : ''}
              </span>
            </div>
          ))}

          <div className="flex items-center gap-3 border-t border-rule pt-3">
            <span className="w-20 shrink-0 text-sm text-ink-2">Coins</span>
            <span aria-hidden="true" className="text-ink-3">
              Rs.
            </span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              aria-label="Total value of coins"
              value={counts.coins ?? ''}
              onChange={(e) => set('coins', e.target.value)}
              placeholder="0"
              className="h-11 w-24 rounded-lg border border-rule bg-surface-3 px-3 text-ink text-center tabular-nums focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
            <span className="ml-auto text-xs text-ink-3">total value, not the count</span>
          </div>
        </div>

        <div className="mt-4 flex items-baseline justify-between border-t border-rule pt-3">
          <span className="text-sm font-medium text-ink">Counted</span>
          <span className="text-2xl font-semibold tabular-nums text-ink">{formatPKR(counted)}</span>
        </div>
      </div>

      <label className="mt-4 block">
        <span className="mb-1.5 block text-sm font-medium text-ink-2">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Anything worth recording about this shift"
          className="w-full rounded-lg border border-rule bg-surface-3 px-3.5 text-ink py-2.5 text-[15px] placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
      </label>

      {/*
        Unpaid bills do not refuse the close — a cashier cannot be kept at the
        till because a table is still eating. The first attempt comes back
        saying what is outstanding; the same button then hands those bills to
        the next cashier, who settles them onto their own shift.
      */}
      {unpaidBlock ? (
        <div className="mt-4 rounded-lg bg-warn-bg px-3.5 py-3 text-sm">
          <p className="font-medium text-warn">
            {`${unpaidCount} unpaid ${unpaidCount === 1 ? 'bill' : 'bills'} on this shift${
              outstanding != null ? ` — ${formatPKR(outstanding)}` : ''
            }.`}
          </p>
          <p className="mt-1 text-ink-2">
            Leave them for the next cashier to settle. The money and the sale go to whoever collects
            — your drawer only answers for what you took.
          </p>
        </div>
      ) : (
        error && (
          <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
            {error.message}
          </p>
        )
      )}

      <div className="mt-5 flex gap-2">
        <Button variant="secondary" size="lg" onClick={onCancel} disabled={busy}>
          Back
        </Button>
        <Button size="lg" className="flex-1" busy={busy} onClick={() => onClose(closeBody(unpaidBlock))}>
          {unpaidBlock ? 'Close and hand over' : 'Close shift'}
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-surface p-3 text-center">
      <dt className="text-xs text-ink-3">{label}</dt>
      <dd className="mt-0.5 font-semibold tabular-nums text-ink">{value}</dd>
    </div>
  );
}

/**
 * What the count came to, against what was expected.
 *
 * A difference is stated plainly and without accusation. Most are a miscount
 * or change given wrong, and a screen that reads like an allegation makes
 * people hide them rather than report them.
 */
function ShiftClosed({ result, onDone }) {
  const s = result.summary ?? result;
  const exact = Math.abs(Number(s.variance ?? 0)) < 0.01;

  return (
    <div className="mx-auto max-w-md p-4 text-center">
      {/* The report itself — the same slip the Reports tab reprints. This, and
          only this, is what prints on the roll; the buttons and advice below
          sit outside it, so they never reach the paper. */}
      <ShiftReport result={result} />

      {!exact && (
        <p className="no-print mt-3 text-sm text-ink-2">
          Tell your manager. Most differences are a miscount or change given wrong, and they are
          easier to trace today than tomorrow.
        </p>
      )}

      {/* The shift is over, so the till hands over to whoever is next rather
          than dropping this person back onto a till they have closed. Marked
          no-print so it does not feed blank paper after the report. */}
      <p className="no-print mt-6 text-sm text-ink-3">
        Signing out returns the till to the login screen for the next person.
      </p>
      <div className="no-print mt-2 flex gap-2">
        <Button variant="secondary" size="lg" className="flex-1" onClick={() => window.print()}>
          Print
        </Button>
        <Button size="lg" className="flex-1" onClick={onDone}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
